interface NaturalLanguageInputAnalysis {
    wordCount: number;
    hasQuestionMark: boolean;
    startsWithQuestionWord: boolean;
    startsWithQuestionStarter: boolean;
    connectorRatio: number;
    hasVerbs: boolean;
    averageWordLength: number;
    hasCommonPhrases: boolean;
}

enum NaturalLanguageQueryType {
    NaturalLanguageQuestion = "NaturalLanguageQuestion",
    KeywordSearch = "KeywordSearch",
    Ambiguous = "Ambiguous",
    Unknown = "Unknown"
}

class NaturalLanguageTypeDetector {
    private static readonly questionWords = new Set([
        // Português
        "o que", "que", "qual", "quais", "quando", "onde", "como", "por que", "porque",
        "quem", "quanto", "quantos", "quantas", "aonde", "de onde",

        // Inglês
        "what", "which", "when", "where", "how", "why", "who", "whom", "whose",
        "how much", "how many", "how long",

        // Espanhol
        "qué", "cuál", "cuáles", "cuándo", "dónde", "cómo", "por qué", "porque",
        "quién", "quiénes", "cuánto", "cuántos", "cuánta", "cuántas", "adónde", "de dónde",

        // Francês
        "quoi", "que", "qu'est-ce que", "quel", "quelle", "quels", "quelles", "quand",
        "où", "comment", "pourquoi", "qui", "combien", "combien de",

        // Italiano
        "cosa", "che cosa", "che", "quale", "quali", "quando", "dove", "come",
        "perché", "chi", "quanto", "quanti", "quanta", "quante",

        // Alemão
        "was", "welche", "welcher", "welches", "wann", "wo", "wie", "warum",
        "wer", "wen", "wem", "wessen", "wie viel", "wie viele", "wie lange"
    ]);

    private static readonly questionStarters = new Set([
        // Português
        "é", "são", "está", "estão", "foi", "foram", "será", "serão", "tem", "têm",
        "pode", "podem", "deve", "devem", "consegue", "conseguem", "existe", "existem",
        "há", "posso", "devo", "preciso", "gostaria", "você", "vocês",

        // Inglês
        "is", "are", "was", "were", "will", "would", "could", "should", "can", "do",
        "does", "did", "have", "has", "had", "am", "i", "you",

        // Espanhol
        "es", "son", "está", "están", "fue", "fueron", "será", "serán", "tiene", "tienen",
        "puede", "pueden", "debe", "deben", "consigue", "consiguen", "existe", "existen",
        "hay", "puedo", "debo", "necesito", "me gustaría", "tú", "usted", "ustedes",

        // Francês
        "est", "sont", "était", "étaient", "sera", "seront", "a", "ont", "avez", "avais",
        "peut", "peuvent", "dois", "doit", "doivent", "existe", "existent", "il y a",
        "je", "tu", "vous", "nous", "peux", "veux", "voudrais",

        // Italiano
        "è", "sono", "era", "erano", "sarà", "saranno", "ha", "hanno", "avete", "aveva",
        "può", "possono", "deve", "devono", "riesce", "riescono", "esiste", "esistono",
        "c'è", "ci sono", "io", "tu", "voi", "noi", "posso", "voglio", "vorrei",

        // Alemão
        "ist", "sind", "war", "waren", "wird", "werden", "hat", "haben", "hatte", "hatten",
        "kann", "können", "muss", "müssen", "schafft", "schaffen", "gibt", "gibt es",
        "ich", "du", "sie", "wir", "ihr", "könnte", "möchte", "würde"
    ]);

    private static readonly commonConnectors = new Set([
        // Português
        "e", "ou", "mas", "porém", "então", "assim", "também", "ainda", "já",
        "para", "com", "sem", "sobre", "entre", "durante", "depois", "antes",
        "de", "da", "do", "das", "dos", "em", "na", "no", "nas", "nos",
        "a", "à", "ao", "às", "aos", "um", "uma", "uns", "umas", "o", "os", "as",

        // Inglês
        "and", "or", "but", "so", "then", "also", "still", "already", "for",
        "with", "without", "about", "between", "during", "after", "before",
        "of", "in", "on", "at", "to", "from", "by", "the", "a", "an",

        // Espanhol
        "y", "o", "pero", "entonces", "así", "también", "aún", "ya",
        "para", "con", "sin", "sobre", "entre", "durante", "después", "antes",
        "de", "del", "de la", "de los", "de las", "en", "en el", "en la", "en los", "en las",
        "a", "al", "a la", "a los", "a las", "un", "una", "unos", "unas", "el", "la", "los", "las",

        // Francês
        "et", "ou", "mais", "donc", "alors", "aussi", "encore", "déjà",
        "pour", "avec", "sans", "sur", "entre", "pendant", "après", "avant",
        "de", "du", "de la", "des", "dans", "en", "à", "au", "aux",
        "un", "une", "des", "le", "la", "les",

        // Italiano
        "e", "o", "ma", "quindi", "allora", "anche", "ancora", "già",
        "per", "con", "senza", "su", "tra", "durante", "dopo", "prima",
        "di", "del", "della", "dei", "delle", "in", "nel", "nella", "nei", "nelle",
        "a", "al", "alla", "ai", "alle", "un", "una", "dei", "il", "la", "gli", "le",

        // Alemão
        "und", "oder", "aber", "so", "dann", "auch", "noch", "schon",
        "für", "mit", "ohne", "über", "zwischen", "während", "nach", "vor",
        "von", "vom", "der", "des", "den", "dem", "in", "im", "am", "zur", "zum",
        "ein", "eine", "einen", "einem", "einer", "das", "die", "der"
    ]);

    public static detectQueryType(input: string): NaturalLanguageQueryType {
        if (!input?.trim()) {
            return NaturalLanguageQueryType.Unknown;
        }

        const analysis = this.analyzeInput(input.trim());
        return this.determineQueryType(analysis);
    }

    private static analyzeInput(input: string): NaturalLanguageInputAnalysis {
        const words = input.toLowerCase()
            .split(/\s+/)
            .filter(word => word.length > 0);

        const analysis: NaturalLanguageInputAnalysis = {
            wordCount: words.length,
            hasQuestionMark: input.includes('?') || input.includes('¿'),
            startsWithQuestionWord: this.startsWithQuestionWord(input),
            startsWithQuestionStarter: this.startsWithQuestionStarter(input),
            connectorRatio: this.calculateConnectorRatio(words),
            hasVerbs: this.hasVerbs(input),
            averageWordLength: words.length > 0 ? words.reduce((sum, word) => sum + word.length, 0) / words.length : 0,
            hasCommonPhrases: this.hasCommonQuestionPhrases(input)
        };

        return analysis;
    }

    private static startsWithQuestionWord(input: string): boolean {
        const lowerInput = input.toLowerCase();
        return Array.from(this.questionWords).some(qw =>
            lowerInput.startsWith(qw + " ") || lowerInput === qw
        );
    }

    private static startsWithQuestionStarter(input: string): boolean {
        const lowerInput = input.toLowerCase();
        return Array.from(this.questionStarters).some(qs =>
            lowerInput.startsWith(qs + " ")
        );
    }

    private static calculateConnectorRatio(words: string[]): number {
        if (words.length === 0) return 0;

        const connectorCount = words.filter(word => this.commonConnectors.has(word)).length;
        return connectorCount / words.length;
    }

    private static hasVerbs(input: string): boolean {
        const verbPatterns = [
            // Português
            /\b(sou|é|são|está|estão|foi|foram|será|serão|tem|têm|tenho|posso|pode|podem|devo|deve|devem|quero|quer|querem|vou|vai|vão|faço|faz|fazem)\b/i,

            // Inglês
            /\b(am|is|are|was|were|will|would|could|should|can|do|does|did|have|has|had|want|wants|go|goes|make|makes)\b/i,

            // Espanhol
            /\b(soy|es|son|está|están|fue|fueron|será|serán|tiene|tienen|tengo|puedo|puede|pueden|debo|debe|deben|quiero|quiere|quieren|voy|va|van|hago|hace|hacen)\b/i,

            // Francês
            /\b(suis|est|sont|était|étaient|sera|seront|ai|as|a|ont|avez|peux|peut|peuvent|dois|doit|doivent|veux|veut|veulent|vais|va|vont|fais|fait|font)\b/i,

            // Italiano
            /\b(sono|è|siamo|era|erano|sarà|saranno|ho|hai|ha|hanno|posso|può|possono|devo|deve|devono|voglio|vuole|vogliono|vado|va|vanno|faccio|fa|fanno)\b/i,

            // Alemão
            /\b(bin|ist|sind|war|waren|wird|werden|habe|hast|hat|haben|kann|könnt|können|muss|musst|müssen|will|willst|wollen|gehe|geht|gehen|mache|macht|machen)\b/i
        ];

        return verbPatterns.some(pattern => pattern.test(input));
    }

    private static hasCommonQuestionPhrases(input: string): boolean {
        const questionPhrases = [
            // Português
            "como fazer", "o que é", "qual é", "onde fica", "quando acontece", "por que", "como funciona",

            // Inglês
            "what is", "how to", "where is", "when does", "why does", "how does",

            // Espanhol
            "cómo hacer", "qué es", "cuál es", "dónde está", "cuándo ocurre", "por qué", "cómo funciona",

            // Francês
            "comment faire", "qu'est-ce que", "quel est", "où se trouve", "quand arrive", "pourquoi", "comment fonctionne",

            // Italiano
            "come fare", "cos'è", "qual è", "dove si trova", "quando accade", "perché", "come funziona",

            // Alemão
            "wie macht man", "was ist", "welche ist", "wo ist", "wann passiert", "warum", "wie funktioniert"
        ];

        const lowerInput = input.toLowerCase();
        return questionPhrases.some(phrase => lowerInput.includes(phrase));
    }

    private static determineQueryType(analysis: NaturalLanguageInputAnalysis): NaturalLanguageQueryType {
        let naturalLanguageScore = 0;

        // Indicadores fortes de linguagem natural
        if (analysis.hasQuestionMark) naturalLanguageScore += 3;
        if (analysis.startsWithQuestionWord) naturalLanguageScore += 4;
        if (analysis.startsWithQuestionStarter) naturalLanguageScore += 3;
        if (analysis.hasCommonPhrases) naturalLanguageScore += 3;
        if (analysis.hasVerbs) naturalLanguageScore += 2;

        // Indicadores baseados em estrutura
        if (analysis.wordCount >= 4) naturalLanguageScore += 2;
        if (analysis.wordCount >= 7) naturalLanguageScore += 1;
        if (analysis.connectorRatio > 0.15) naturalLanguageScore += 2;
        if (analysis.averageWordLength > 4) naturalLanguageScore += 1;

        // Indicadores negativos (sugerem busca por palavras-chave)
        if (analysis.wordCount <= 2 && !analysis.hasQuestionMark) naturalLanguageScore -= 2;
        if (analysis.connectorRatio === 0 && analysis.wordCount > 3) naturalLanguageScore -= 1;
        if (analysis.averageWordLength < 3) naturalLanguageScore -= 1;

        // Decisão final
        if (naturalLanguageScore >= 5) {
            return NaturalLanguageQueryType.NaturalLanguageQuestion;
        } else if (naturalLanguageScore <= 1) {
            return NaturalLanguageQueryType.KeywordSearch;
        } else {
            return NaturalLanguageQueryType.Ambiguous;
        }
    }

    public static getDetectionReason(input: string): string {
        const analysis = this.analyzeInput(input.trim());
        const reasons: string[] = [];

        if (analysis.hasQuestionMark) reasons.push("Contém ponto de interrogação");
        if (analysis.startsWithQuestionWord) reasons.push("Inicia com palavra interrogativa");
        if (analysis.startsWithQuestionStarter) reasons.push("Inicia com verbo/pronome típico de pergunta");
        if (analysis.hasCommonPhrases) reasons.push("Contém frases típicas de pergunta");
        if (analysis.hasVerbs) reasons.push("Contém verbos");
        if (analysis.connectorRatio > 0.15) reasons.push("Boa proporção de conectores");
        if (analysis.wordCount >= 7) reasons.push("Frase relativamente longa");

        if (analysis.wordCount <= 2) reasons.push("Muito poucas palavras");
        if (analysis.connectorRatio === 0 && analysis.wordCount > 3) reasons.push("Falta de conectores");

        return reasons.join("; ");
    }
}

// Exportar para uso como módulo
export { NaturalLanguageQueryType, NaturalLanguageTypeDetector };

export type { NaturalLanguageInputAnalysis };
